import * as React from 'react';
import { useState } from 'react';
import { Building2, CircleAlert, Phone, User, Warehouse } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AddressComplementPart } from '@klareospace/pharos-lib-front-utils';
import { extractAddressComplementParts } from '@klareospace/pharos-lib-front-utils';
import { countryFromDialCode, formatNationalNumber } from '@klareospace/pharos-lib-front-utils/utils';
import {
  ORDER_STATUS_REASON,
  ORDER_UPDATE_FIELD_CATEGORY,
  TRANSPORT_TYPE,
  type Order,
} from '@klareospace/pharos-lib-types';
import {
  formatDeliveryAddress,
  formatRequestedAddress,
  resolveDeliveryPostalAddress,
} from '@klareospace/pharos-lib-utils';
import {
  DRAWER_FIELD_LABEL_CLASSES,
  DrawerSection,
  ErrorBanner,
  FormSectionHeader,
  IconText,
  TextCell,
} from '@klareospace/pharos-lib-web-utils';

import {
  ADDRESS_ANOMALY_SIDE,
  resolveAddressAnomalySide,
  resolveDeliveryIcon,
  resolvePickupIcon,
  translateStatusReason,
} from '../utils';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { useOrderEditability } from '../hooks/use-order-editability';
import type { TimeWindowEditTarget } from '../hooks/use-time-window-edit';
import { AddressComplementParts } from './address-complement-parts';
import { AddressEditDialog } from './address-edit-dialog';
import type { AddressEditVariant } from '../hooks/use-address-edit-form';
import { DurationTimeInlineEditor } from './duration-time-inline-editor';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { TimeSlotField } from './time-slot-field';
import { TimeWindowEditDialog } from './time-window-edit-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PickupDeliverySectionProps {
  readonly order: Order;
}

// ─── Shared sub-blocks ────────────────────────────────────────────────────────

interface AddressErrorLineProps {
  readonly error: string;
}

/** Renders an inline error line beneath an address. */
function AddressErrorLine({ error }: AddressErrorLineProps): React.JSX.Element {
  return (
    <span className="flex items-center gap-1 text-[11px] text-destructive">
      <CircleAlert className="w-3 h-3 shrink-0" />
      {error}
    </span>
  );
}

interface AddressLineBlockProps {
  readonly addressLabel: string;
  readonly complementParts: AddressComplementPart[];
  readonly error?: string;
  readonly icon: React.ElementType;
}

/** Renders the address text next to its leading delivery icon, complement parts, and an optional inline error line. */
function AddressLineBlock({ addressLabel, complementParts, error, icon }: AddressLineBlockProps): React.JSX.Element {
  return (
    <>
      <IconText icon={icon} size="sm">
        <span className={`text-sm break-words ${error !== undefined ? 'text-destructive' : 'text-foreground'}`}>
          {addressLabel}
        </span>
      </IconText>
      <AddressComplementParts parts={complementParts} />
      {error !== undefined && <AddressErrorLine error={error} />}
    </>
  );
}

interface LocationBlockProps {
  readonly address?: Order['delivery']['address'];
  readonly contact?: Order['pickup']['contact'] | Order['delivery']['contact'];
  readonly error?: string;
  readonly fieldState: OrderFieldEditState;
  readonly icon: React.ElementType;
  readonly iconColor?: string;
  readonly lockReason: OrderFieldLockReason | null;
  readonly onEdit: () => void;
  readonly requestedAddress?: Order['delivery']['requestedAddress'];
  readonly siteName?: string;
}

/**
 * Resolves the address display label, tolerant of legacy-shape (no `location`,
 * no `originalAddress`) delivery addresses, with the client-submitted requestedAddress
 * as a final fallback when the address itself is unresolved (empty).
 */
function resolveAddressLabel(
  address: Order['delivery']['address'],
  requestedAddress: Order['delivery']['requestedAddress'],
): string {
  return formatDeliveryAddress(address) || formatRequestedAddress(requestedAddress);
}

function LocationBlock({
  address,
  contact,
  error,
  fieldState,
  icon: Icon,
  iconColor,
  lockReason,
  onEdit,
  requestedAddress,
  siteName,
}: LocationBlockProps): React.JSX.Element {
  // C6: computed only when the address branch is reached (siteName wins over address/requestedAddress)
  const addressLabel = siteName === undefined ? resolveAddressLabel(address, requestedAddress) : '';
  const complementParts =
    siteName === undefined && address !== undefined
      ? extractAddressComplementParts(resolveDeliveryPostalAddress(address))
      : [];

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={onEdit}>
      <div className="flex flex-col gap-0.5 w-full">
        {siteName !== undefined ? (
          <IconText
            className="text-foreground"
            icon={Icon}
            iconStyle={iconColor !== undefined ? { color: iconColor } : undefined}
            size="sm"
          >
            <span>{siteName}</span>
          </IconText>
        ) : addressLabel !== '' ? (
          <AddressLineBlock addressLabel={addressLabel} complementParts={complementParts} error={error} icon={Icon} />
        ) : (
          <TextCell>—</TextCell>
        )}
        {contact != null && (
          <>
            <IconText className="text-foreground mt-1" icon={User} size="xs">
              <span className="truncate">{contact.name}</span>
            </IconText>
            {contact.phone !== undefined && (
              <IconText className="text-muted-foreground" icon={Phone} size="xs">
                <span>
                  {contact.phone.dialCode}{' '}
                  {formatNationalNumber(contact.phone.number, countryFromDialCode(contact.phone.dialCode))}
                </span>
              </IconText>
            )}
          </>
        )}
      </div>
    </EditableFieldWrapper>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PickupDeliverySection({ order }: PickupDeliverySectionProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { delivery, pickup } = order;
  const { getFieldState, lockReason } = useOrderEditability(order);

  const [addressDialogVariant, setAddressDialogVariant] = useState<AddressEditVariant | null>(null);
  const [timeWindowDialogTarget, setTimeWindowDialogTarget] = useState<TimeWindowEditTarget | null>(null);

  const PickupIcon = resolvePickupIcon(pickup.site);
  const DeliveryIcon = resolveDeliveryIcon(delivery.site);

  // Derives which leg (pickup or delivery) carries the address anomaly, so the
  // banner renders alongside the address block that actually failed instead of
  // at a fixed section-level location.
  const errorSide = resolveAddressAnomalySide(order);
  const isPickupAddressError = errorSide === ADDRESS_ANOMALY_SIDE.PICKUP;
  const isDeliveryAddressError = errorSide === ADDRESS_ANOMALY_SIDE.DELIVERY;

  // Non-impacting so the pencil stays available through IN_PROGRESS; the dialog gates the impacting subfields itself.
  const addressFieldState = getFieldState(ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING);
  const planningImpactingFieldState = getFieldState(ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING);

  const pickupTimeslotTarget = resolveTimeslotTarget(order, TRANSPORT_TYPE.PICKUP);
  const deliveryTimeslotTarget = resolveTimeslotTarget(order, TRANSPORT_TYPE.DELIVERY);

  const addressAnomalyMessage = translateStatusReason(t, ORDER_STATUS_REASON.GEOCODING_FAILED) ?? '';

  return (
    <>
      <DrawerSection icon={Warehouse} title={t('drawer.pickup')}>
        {/* Pickup grid — pickup.site is always defined */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.address')}</p>
            <LocationBlock
              contact={pickup.contact}
              error={isPickupAddressError ? t('drawer.errors.addressIssue') : undefined}
              fieldState={addressFieldState}
              icon={PickupIcon}
              iconColor={pickup.site.color}
              lockReason={lockReason}
              onEdit={(): void => setAddressDialogVariant('pickup')}
              siteName={pickup.site.name}
            />
          </div>
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.timeslot')}</p>
            <TimeSlotField
              fieldState={planningImpactingFieldState}
              lockReason={lockReason}
              onEdit={(): void => setTimeWindowDialogTarget(pickupTimeslotTarget)}
              timeSlot={pickup.timeSlot}
            />
          </div>
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.pickupDuration')}</p>
            <DurationTimeInlineEditor
              buildPatch={(value) => ({ pickup: { durationTime: value } })}
              durationTime={pickup.durationTime}
              fieldState={planningImpactingFieldState}
              lockReason={lockReason}
              order={order}
            />
          </div>
        </div>
        {isPickupAddressError && <ErrorBanner className="mt-2" message={addressAnomalyMessage} />}

        {/* Inset divider */}
        <div className="my-4 border-t border-border" />

        {/* Delivery sub-header */}
        <FormSectionHeader icon={Building2} title={t('drawer.delivery')} />
        {isDeliveryAddressError && <ErrorBanner className="mt-2" message={addressAnomalyMessage} />}

        {/* Delivery grid */}
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.address')}</p>
            <LocationBlock
              address={delivery.address}
              contact={delivery.contact}
              error={isDeliveryAddressError ? t('drawer.errors.addressIssue') : undefined}
              fieldState={addressFieldState}
              icon={DeliveryIcon}
              iconColor={delivery.site?.color}
              lockReason={lockReason}
              onEdit={(): void => setAddressDialogVariant('delivery')}
              requestedAddress={delivery.requestedAddress}
              siteName={delivery.site?.name}
            />
          </div>
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.timeslot')}</p>
            <TimeSlotField
              fieldState={planningImpactingFieldState}
              lockReason={lockReason}
              onEdit={(): void => setTimeWindowDialogTarget(deliveryTimeslotTarget)}
              timeSlot={delivery.timeSlot}
            />
          </div>
          <div className="min-w-0">
            <p className={DRAWER_FIELD_LABEL_CLASSES}>{t('drawer.deliveryDuration')}</p>
            <DurationTimeInlineEditor
              buildPatch={(value) => ({ delivery: { durationTime: value } })}
              durationTime={delivery.durationTime}
              fieldState={planningImpactingFieldState}
              lockReason={lockReason}
              order={order}
            />
          </div>
        </div>
      </DrawerSection>

      {addressDialogVariant !== null && (
        <AddressEditDialog
          onClose={(): void => setAddressDialogVariant(null)}
          open
          order={order}
          variant={addressDialogVariant}
        />
      )}
      {timeWindowDialogTarget !== null && (
        <TimeWindowEditDialog
          onClose={(): void => setTimeWindowDialogTarget(null)}
          open
          order={order}
          target={timeWindowDialogTarget}
        />
      )}
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** The matching leg binds to `requestedTimeWindow`; the other leg patches its own timeslot field directly. */
function resolveTimeslotTarget(order: Order, leg: Order['transportType']): TimeWindowEditTarget {
  if (order.transportType === leg) return 'requestedTimeWindow';
  return leg === TRANSPORT_TYPE.PICKUP ? 'pickupTimeSlot' : 'deliveryTimeSlot';
}
